/**
 * DialogSections.tsx - Reusable Dialog Content Sections
 * 
 * CHANGE HISTORY:
 * - 251025_1740 danielbeer: Migrated from Promptr with TypeScript types
 * 
 * Pre-built sections for common dialog content patterns
 */

import React, { ReactNode } from 'react';
import './DialogSections.css';

/**
 * InfoBox Props
 */
export interface InfoBoxProps {
  /** Icon or emoji */
  icon?: ReactNode;
  /** Content */
  children: ReactNode;
  /** Visual variant */
  variant?: 'info' | 'success' | 'warning' | 'error';
  /** Additional CSS class */
  className?: string;
}

/**
 * InfoBox - Highlighted information box
 * 
 * @example
 * ```tsx
 * <InfoBox icon="ℹ️" variant="info">
 *   This is important information
 * </InfoBox>
 * ```
 */
export const InfoBox: React.FC<InfoBoxProps> = ({ 
  icon, 
  children, 
  variant = 'info',
  className = ''
}) => (
  <div className={`dialog-infobox dialog-infobox-${variant} ${className}`}>
    {icon && <div className="infobox-icon">{icon}</div>}
    <div className="infobox-content">{children}</div>
  </div>
);

/**
 * OptionGroup Props
 */
export interface OptionGroupProps {
  /** Options */
  children: ReactNode;
  /** Additional CSS class */
  className?: string;
}

/**
 * OptionGroup - Radio or checkbox option group
 * 
 * @example
 * ```tsx
 * <OptionGroup>
 *   <Option type="radio" name="choice" value="a" title="Option A" />
 *   <Option type="radio" name="choice" value="b" title="Option B" />
 * </OptionGroup>
 * ```
 */
export const OptionGroup: React.FC<OptionGroupProps> = ({ 
  children, 
  className = '' 
}) => (
  <div className={`dialog-option-group ${className}`}>
    {children}
  </div>
);

/**
 * Option Props
 */
export interface OptionProps {
  /** Input type */
  type?: 'radio' | 'checkbox';
  /** Input name */
  name?: string;
  /** Input value */
  value?: string;
  /** Checked state */
  checked?: boolean;
  /** Change handler */
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  /** Disabled state */
  disabled?: boolean;
  /** Option title */
  title?: string;
  /** Option description */
  description?: string;
  /** Badge text */
  badge?: string;
  /** Warning text */
  warning?: string;
  /** Auto focus */
  autoFocus?: boolean;
  /** Custom content */
  children?: ReactNode;
}

/**
 * Option - Single selectable option
 * 
 * @example
 * ```tsx
 * <Option
 *   type="radio"
 *   name="framework"
 *   value="react"
 *   title="React"
 *   description="A JavaScript library for building user interfaces"
 *   badge="Popular"
 * />
 * ```
 */
export const Option: React.FC<OptionProps> = ({ 
  type = 'radio',
  name,
  value,
  checked,
  onChange,
  disabled = false,
  title,
  description,
  badge,
  warning,
  autoFocus = false,
  children
}) => (
  <label className={`dialog-option ${disabled ? 'disabled' : ''}`}>
    <input
      type={type}
      name={name}
      value={value}
      checked={checked}
      onChange={onChange}
      disabled={disabled}
      autoFocus={autoFocus}
      className="option-input"
    />
    <div className="option-content">
      {children || (
        <>
          <div className="option-title">
            {title}
            {badge && <span className="option-badge">{badge}</span>}
          </div>
          {description && <div className="option-description">{description}</div>}
          {warning && <div className="option-warning">{warning}</div>}
        </>
      )}
    </div>
  </label>
);

/**
 * Preview Props
 */
export interface PreviewProps {
  /** Icon or emoji */
  icon?: ReactNode;
  /** Preview title */
  title?: string;
  /** Preview subtitle */
  subtitle?: string;
  /** Preview content */
  children?: ReactNode;
  /** Additional CSS class */
  className?: string;
}

/**
 * Preview - Content preview section
 * 
 * @example
 * ```tsx
 * <Preview
 *   icon="📄"
 *   title="document.pdf"
 *   subtitle="2.4 MB"
 * >
 *   <img src="/preview.png" alt="Preview" />
 * </Preview>
 * ```
 */
export const Preview: React.FC<PreviewProps> = ({ 
  icon, 
  title, 
  subtitle, 
  children, 
  className = '' 
}) => (
  <div className={`dialog-preview ${className}`}>
    {(icon || title || subtitle) && (
      <div className="preview-header">
        {icon && <div className="preview-icon">{icon}</div>}
        {(title || subtitle) && (
          <div className="preview-info">
            {title && <div className="preview-title">{title}</div>}
            {subtitle && <div className="preview-subtitle">{subtitle}</div>}
          </div>
        )}
      </div>
    )}
    {children && <div className="preview-content">{children}</div>}
  </div>
);

/**
 * Notice Props
 */
export interface NoticeProps {
  /** Icon or emoji */
  icon?: ReactNode;
  /** Notice content */
  children: ReactNode;
  /** Visual variant */
  variant?: 'info' | 'success' | 'warning' | 'error';
  /** Additional CSS class */
  className?: string;
}

/**
 * Notice - Important notice or warning
 * 
 * @example
 * ```tsx
 * <Notice icon="⚠️" variant="warning">
 *   This action cannot be undone
 * </Notice>
 * ```
 */
export const Notice: React.FC<NoticeProps> = ({ 
  icon, 
  children, 
  variant = 'info',
  className = ''
}) => (
  <div className={`dialog-notice dialog-notice-${variant} ${className}`}>
    {icon && <div className="notice-icon">{icon}</div>}
    <div className="notice-text">{children}</div>
  </div>
);

/**
 * Field Props
 */
export interface FieldProps {
  /** Field label */
  label?: string;
  /** Error message */
  error?: string;
  /** Required field */
  required?: boolean;
  /** Field input */
  children: ReactNode;
  /** Additional CSS class */
  className?: string;
}

/**
 * Field - Form field with label
 * 
 * @example
 * ```tsx
 * <Field label="Email" required error={emailError}>
 *   <input type="email" value={email} onChange={handleChange} />
 * </Field>
 * ```
 */
export const Field: React.FC<FieldProps> = ({ 
  label, 
  error, 
  required, 
  children, 
  className = '' 
}) => (
  <div className={`dialog-field ${error ? 'has-error' : ''} ${className}`}>
    {label && (
      <label className="field-label">
        {label}
        {required && <span className="field-required">*</span>}
      </label>
    )}
    <div className="field-input">{children}</div>
    {error && <div className="field-error">{error}</div>}
  </div>
);

/**
 * Divider Props
 */
export interface DividerProps {
  /** Spacing size */
  spacing?: 'small' | 'medium' | 'large';
}

/**
 * Divider - Visual separator
 * 
 * @example
 * ```tsx
 * <Divider spacing="large" />
 * ```
 */
export const Divider: React.FC<DividerProps> = ({ spacing = 'medium' }) => (
  <div className={`dialog-divider dialog-divider-${spacing}`} />
);

/**
 * Section Props
 */
export interface SectionProps {
  /** Section title */
  title?: string;
  /** Section content */
  children: ReactNode;
  /** Additional CSS class */
  className?: string;
}

/**
 * Section - Generic content section with optional title
 * 
 * @example
 * ```tsx
 * <Section title="Advanced Options">
 *   <Field label="Timeout">
 *     <input type="number" />
 *   </Field>
 * </Section>
 * ```
 */
export const Section: React.FC<SectionProps> = ({ 
  title, 
  children, 
  className = '' 
}) => (
  <div className={`dialog-section ${className}`}>
    {title && <h4 className="section-title">{title}</h4>}
    <div className="section-content">{children}</div>
  </div>
);
